import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
  MutationFunction,
  MutationKey,
  UseMutationOptions,
} from "@tanstack/react-query"
import type { QueryKey } from "@tanstack/react-query"

import { toaster } from "@/components/infrastructure"

type DynamicQueryKey<TData, TVariables> =
  | QueryKey
  | ((variables: TVariables, data: TData) => QueryKey)

interface InvalidateConfig<TData, TVariables> {
  queryKey: DynamicQueryKey<TData, TVariables>
  exact?: boolean
}

interface SetQueryDataConfig<TData, TVariables> {
  queryKey: DynamicQueryKey<TData, TVariables>
  updater: (previous: unknown, data: TData, variables: TVariables) => unknown
}

interface ToastConfig {
  title: string
  description?: string
  duration?: number
  type?: "success" | "info" | "warning" | "error"
}

// For backwards compatibility
type SuccessToastConfig = ToastConfig

interface ApiMutationOptions<TData, TError, TVariables, TContext>
  extends Omit<
    UseMutationOptions<TData, TError, TVariables, TContext>,
    "mutationFn" | "onSuccess" | "onError"
  > {
  mutationFn: MutationFunction<TData, TVariables>
  mutationKey?: MutationKey
  invalidateQueries?: Array<
    | InvalidateConfig<TData, TVariables>
    | QueryKey
    | ((variables: TVariables, data: TData) => QueryKey)
  >
  removeQueries?: Array<DynamicQueryKey<TData, TVariables>>
  setQueryData?: Array<SetQueryDataConfig<TData, TVariables>>
  successToast?:
    | SuccessToastConfig
    | ((data: TData, variables: TVariables) => SuccessToastConfig)
  errorToast?:
    | ToastConfig
    | ((error: TError, variables: TVariables) => ToastConfig)
    | boolean // true = show default error toast
  onSuccess?: UseMutationOptions<
    TData,
    TError,
    TVariables,
    TContext
  >["onSuccess"]
  onError?: UseMutationOptions<TData, TError, TVariables, TContext>["onError"]
}

function resolveQueryKey<TData, TVariables>(
  key: DynamicQueryKey<TData, TVariables>,
  variables: TVariables,
  data: TData,
): QueryKey {
  return typeof key === "function" ? key(variables, data) : key
}

function toInvalidateConfig<TData, TVariables>(
  config:
    | InvalidateConfig<TData, TVariables>
    | QueryKey
    | ((variables: TVariables, data: TData) => QueryKey),
  variables: TVariables,
  data: TData,
): InvalidateConfig<TData, TVariables> {
  if (typeof config === "function") {
    return { queryKey: config(variables, data) }
  }
  if (Array.isArray(config)) {
    return { queryKey: config }
  }
  return config as InvalidateConfig<TData, TVariables>
}

/**
 * Extract a user-friendly error message from various error formats
 */
function extractErrorMessage(error: unknown): string {
  if (!error) return "An unexpected error occurred"

  // Handle axios/fetch errors with response data
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>

    // Standard API error response format
    if (err.body && typeof err.body === "object") {
      const body = err.body as Record<string, unknown>
      if (body.detail) return String(body.detail)
      if (body.message) return String(body.message)
    }

    // Direct error properties
    if (err.detail) return String(err.detail)
    if (err.message) return String(err.message)

    // Axios error format
    if (err.response && typeof err.response === "object") {
      const response = err.response as Record<string, unknown>
      if (response.data && typeof response.data === "object") {
        const data = response.data as Record<string, unknown>
        if (data.detail) return String(data.detail)
        if (data.message) return String(data.message)
      }
    }
  }

  // Fallback for string errors
  if (typeof error === "string") return error

  return "An unexpected error occurred"
}

export function useApiMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(options: ApiMutationOptions<TData, TError, TVariables, TContext>) {
  const queryClient = useQueryClient()

  const {
    mutationFn,
    mutationKey,
    invalidateQueries,
    removeQueries,
    setQueryData,
    successToast,
    errorToast,
    onSuccess,
    onError,
    ...mutationOptions
  } = options

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn,
    mutationKey,
    onSuccess: async (data, variables, context) => {
      setQueryData?.forEach(({ queryKey, updater }) => {
        const targetKey = resolveQueryKey(queryKey, variables, data)
        queryClient.setQueryData(targetKey, (previous) =>
          updater(previous, data, variables),
        )
      })

      invalidateQueries?.forEach((entry) => {
        const config = toInvalidateConfig(entry, variables, data)
        const targetKey = resolveQueryKey(config.queryKey, variables, data)
        queryClient.invalidateQueries({
          queryKey: targetKey,
          exact: config.exact,
        })
      })

      removeQueries?.forEach((queryKey) => {
        const targetKey = resolveQueryKey(queryKey, variables, data)
        queryClient.removeQueries({ queryKey: targetKey })
      })

      if (successToast) {
        const toastConfig =
          typeof successToast === "function"
            ? successToast(data, variables)
            : successToast
        const {
          title,
          description,
          duration = 3000,
          type = "success",
        } = toastConfig
        toaster.create({ title, description, duration, type })
      }

      await onSuccess?.(data, variables, context)
    },
    onError: async (error, variables, context) => {
      // Default logging to aid debugging when callers omit custom error handling
      console.error("API mutation error:", error)

      // Show error toast if configured
      if (errorToast) {
        let toastConfig: ToastConfig

        if (errorToast === true) {
          // Default error toast
          toastConfig = {
            title: "Operation Failed",
            description: extractErrorMessage(error),
            type: "error",
          }
        } else if (typeof errorToast === "function") {
          toastConfig = errorToast(error, variables)
        } else {
          toastConfig = errorToast
        }

        const {
          title,
          description,
          duration = 5000,
          type = "error",
        } = toastConfig
        toaster.create({ title, description, duration, type })
      }

      await onError?.(error, variables, context)
    },
    ...mutationOptions,
  })
}
